/**
 * Resolución de tipoDescuento del pedido:
 * 1) snapshot en pedido
 * 2) fallback legacy vía catálogo Cupones (solo si ausente)
 * 3) sin inventar "carrito"
 */

import { normalizeTipoDescuento } from '@/utils/pedidos/surtidoTotals'

/**
 * Lee snapshot del pedido sin I/O.
 * @param {object|null|undefined} pedido
 * @returns {'carrito'|'envio'|null}
 */
export function readPedidoTipoDescuentoSnapshot(pedido) {
  return normalizeTipoDescuento(pedido?.tipoDescuento)
}

/**
 * @param {object|null|undefined} pedido
 * @param {object} deps
 * @param {(name: string) => Promise<{ success: boolean, data?: object|null, error?: string }>} deps.getCouponByName
 * @param {(pedidoKey: string, tipoDescuento: string) => Promise<void>} [deps.persistTipoDescuento]
 * @returns {Promise<{
 *   tipoDescuento: 'carrito'|'envio'|null,
 *   source: 'snapshot'|'legacy_catalog'|'none'|'catalog_miss'|'catalog_error'|'catalog_invalid',
 *   persisted: boolean,
 *   error?: string
 * }>}
 */
export async function resolvePedidoTipoDescuento(pedido, deps = {}) {
  const snapshot = readPedidoTipoDescuentoSnapshot(pedido)
  if (snapshot) {
    return { tipoDescuento: snapshot, source: 'snapshot', persisted: false }
  }

  const cuponCode = pedido?.cupon != null ? String(pedido.cupon).trim() : ''
  if (!cuponCode) {
    return { tipoDescuento: null, source: 'none', persisted: false }
  }

  const getCouponByName = deps.getCouponByName
  if (typeof getCouponByName !== 'function') {
    return {
      tipoDescuento: null,
      source: 'catalog_error',
      persisted: false,
      error: 'getCouponByName no disponible',
    }
  }

  let result
  try {
    result = await getCouponByName(cuponCode)
  } catch (err) {
    return {
      tipoDescuento: null,
      source: 'catalog_error',
      persisted: false,
      error: err?.message || 'Error al consultar cupón',
    }
  }

  if (!result?.success || !result.data) {
    return {
      tipoDescuento: null,
      source: 'catalog_miss',
      persisted: false,
      error: result?.error || 'Cupón no encontrado',
    }
  }

  const tipo = normalizeTipoDescuento(result.data.tipoDescuento)
  if (!tipo) {
    return {
      tipoDescuento: null,
      source: 'catalog_invalid',
      persisted: false,
      error: 'tipoDescuento inválido en catálogo',
    }
  }

  let persisted = false
  const pedidoKey = pedido?.pedidoKey
  const stillAbsent = readPedidoTipoDescuentoSnapshot(pedido) == null
  if (stillAbsent && pedidoKey && typeof deps.persistTipoDescuento === 'function') {
    try {
      await deps.persistTipoDescuento(pedidoKey, tipo)
      persisted = true
    } catch (err) {
      console.warn('No se pudo persistir tipoDescuento legacy en el pedido:', err?.message || err)
    }
  }

  return { tipoDescuento: tipo, source: 'legacy_catalog', persisted }
}
